import type {NameSetsQuery} from '../../../generated/types';

export type NameSet = NameSetsQuery['nameSets'][number];
export type NameGroup = NameSet['groups'][number];

export type GeneratedName = {
	id: string;
	given: string;
	family?: string;
	nickname?: string;
	people?: string;
	parts: Array<{kind: 'given' | 'family' | 'nickname'; text: string}>;
	text: string;
	note?: string;
};

export type NameGender = 'male' | 'female';

export type GenerateOptions = {
	/** код группы личных имён; без него берутся любые, кроме детских */
	givenId: string | null;
	/** народ внутри набора; без него выбирается случайный */
	people: string | null;
	/** пол для прозвищ с мужской и женской формой; без него определяется по группе имён или случайно */
	gender?: NameGender | null;
	count: number;
};

const CHILD_GROUP = 'child';
const DEFAULT_FAMILY_NOTE = 'На всеобщем';
const DEFAULT_GIVEN_NOTE = 'Значение';

export const GENDER_GROUPS = ['male', 'female'];

export const givenGroups = (groups: NameGroup[]) => groups.filter((group) => group.role === 'given');

export const groupsOf = (groups: NameGroup[], role: NameGroup['role']) => groups.filter((group) => group.role === role);

export const peoplesOf = (groups: NameGroup[]): string[] => [
	...new Set(groups.map((group) => group.people).filter((people): people is string => !!people)),
];

export const groupsOfPeople = (groups: NameGroup[], people: string | null) =>
	people ? groups.filter((group) => !group.people || group.people === people) : groups;

export const uniqueByCode = (groups: NameGroup[]) =>
	groups.filter((group, index) => groups.findIndex((other) => other.code === group.code) === index);

export const hasGenderedForms = (groups: NameGroup[]) =>
	groups.some((group) => group.items.some((item) => item.includes('|')));

export const formsLabel = (item: string) => item.replace('|', ' / ');

const genderedForm = (item: string, gender: NameGender) => {
	const forms = item.split('|');
	return forms.length > 1 && gender === 'female' ? forms[1] : forms[0];
};

export function splitNote(item: string): {name: string; note?: string} {
	const match = item.match(/^(.*?)\s*\((.+)\)$/);
	return match ? {name: match[1], note: match[2]} : {name: item};
}

const pick = <T>(list: T[], random: () => number): T => list[Math.floor(random() * list.length)];

function composeParts(
	{given, family, nickname}: Pick<GeneratedName, 'given' | 'family' | 'nickname'>,
	familyFirst: boolean,
): GeneratedName['parts'] {
	const parts: GeneratedName['parts'] = [{kind: 'given', text: given}];
	if (nickname) parts.push({kind: 'nickname', text: `«${nickname}»`});
	if (family) parts[familyFirst ? 'unshift' : 'push']({kind: 'family', text: family});
	return parts;
}

const composeText = (name: Pick<GeneratedName, 'given' | 'family' | 'nickname'>, familyFirst: boolean) =>
	composeParts(name, familyFirst)
		.map((part) => part.text)
		.join(' ');

export function generateNames(
	groups: NameGroup[],
	{givenId, people, gender, count}: GenerateOptions,
	random: () => number = Math.random,
): GeneratedName[] {
	const peoples = people ? [people] : peoplesOf(groups);
	const result: GeneratedName[] = [];
	const seen = new Set<string>();

	for (let attempt = 0; attempt < count * 20 && result.length < count; attempt++) {
		const chosenPeople = peoples.length ? pick(peoples, random) : null;
		const scope = groupsOfPeople(groups, chosenPeople);

		const given = givenGroups(scope).filter((group) => (givenId ? group.code === givenId : group.code !== CHILD_GROUP));
		const pool = given.length ? given : givenGroups(scope);
		const families = groupsOf(scope, 'family');
		const nicknames = groupsOf(scope, 'nickname');
		if (!pool.length) continue;

		const givenGroup = pick(pool, random);
		const nameGender: NameGender = GENDER_GROUPS.includes(givenGroup.code)
			? (givenGroup.code as NameGender)
			: gender ?? (random() < 0.5 ? 'male' : 'female');

		const givenEntry = splitNote(genderedForm(pick(givenGroup.items, random), nameGender));
		const familyGroup = families.length ? pick(families, random) : null;
		const familyEntry = familyGroup ? splitNote(genderedForm(pick(familyGroup.items, random), nameGender)) : null;
		const nicknameGroup = nicknames.length ? pick(nicknames, random) : null;

		const entry = {
			given: givenEntry.name,
			family: familyEntry?.name,
			nickname: nicknameGroup ? genderedForm(pick(nicknameGroup.items, random), nameGender) : undefined,
		};
		const familyFirst = !!familyGroup?.familyFirst;
		const text = composeText(entry, familyFirst);
		if (seen.has(text)) continue;
		seen.add(text);

		const notes: string[] = [];
		if (givenEntry.note) notes.push(`${givenGroup.noteLabel ?? DEFAULT_GIVEN_NOTE}: ${givenEntry.note}`);
		if (familyEntry?.note) {
			notes.push(`${familyGroup?.noteLabel ?? DEFAULT_FAMILY_NOTE}: ${composeText({...entry, family: familyEntry.note}, familyFirst)}`);
		}
		if (chosenPeople && !people) notes.push(chosenPeople);

		result.push({
			id: text,
			...entry,
			people: chosenPeople ?? undefined,
			parts: composeParts(entry, familyFirst),
			text,
			note: notes.join(' · ') || undefined,
		});
	}

	return result;
}
